import React, { useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { TweetSettings } from './TweetSettings';
import {
  TweetSettingsValues,
  loadTweetSettingsFromStorage,
} from '../lib/tweetSettings';
import { useT } from '../lib/i18n';
import { useToast } from './Toast';

interface TweetEditorProps {
  initialText: string;
  onSave: (editedText: string, settings?: TweetSettingsValues) => void;
  onCancel: () => void;
  initialSettings?: TweetSettingsValues;
}

export const TweetEditor: React.FC<TweetEditorProps> = ({
  initialText,
  onSave,
  onCancel,
  initialSettings,
}) => {
  const { t } = useT();
  const { toast } = useToast();
  const [text, setText] = useState(initialText);
  const [charCount, setCharCount] = useState(initialText.length);
  const [settings, setSettings] = useState<TweetSettingsValues>(
    initialSettings || loadTweetSettingsFromStorage()
  );
  const maxChars = 280;

  useEffect(() => {
    setCharCount(text.length);
  }, [text]);

  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  const handleSave = () => {
    if (text.trim().length === 0) {
      toast(t('tweetEditor.emptyError'), 'warning');
      return;
    }
    if (text.length > maxChars) {
      toast(t('tweetEditor.tooLongError'), 'warning');
      return;
    }
    onSave(text, settings);
  };

  const isOverLimit = charCount > maxChars;
  const isNearLimit = charCount > maxChars - 20;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-surface-2 border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border">
          <h2 className="text-lg sm:text-xl font-bold text-text-primary">
            {t('tweetEditor.title')}
          </h2>
          <button
            onClick={onCancel}
            className="text-text-muted hover:text-text-primary transition-colors text-2xl leading-none"
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        {/* Editor */}
        <div className="p-4 sm:p-6 space-y-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('tweetEditor.placeholder')}
            className="w-full min-h-[200px] p-4 bg-bg border border-border rounded-xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] focus:outline-none focus:ring-2 focus:ring-brand transition-shadow"
            autoFocus
          />

          {/* Character Count */}
          <div className="flex items-center justify-between">
            <div className="text-xs sm:text-sm text-text-muted">
              {t('tweetEditor.tip')}
            </div>
            <div
              className={cn(
                'text-sm sm:text-base font-bold tabular-nums',
                isOverLimit
                  ? 'text-danger'
                  : isNearLimit
                    ? 'text-warning'
                    : 'text-text-muted'
              )}
            >
              {charCount} / {maxChars}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-1.5 bg-border rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-300',
                isOverLimit
                  ? 'bg-danger'
                  : isNearLimit
                    ? 'bg-warning'
                    : 'bg-brand'
              )}
              style={{ width: `${Math.min(100, (charCount / maxChars) * 100)}%` }}
            />
          </div>

          {/* Tweet Settings */}
          <TweetSettings
            value={settings}
            onChange={setSettings}
            title={t('tweetEditor.strategySettings')}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 sm:p-5 border-t border-border">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-bg border border-border rounded-xl text-text-primary font-medium hover:bg-surface-2 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isOverLimit || text.trim().length === 0}
            className={cn(
              'flex-1 py-3 px-4 rounded-xl font-bold transition-all',
              isOverLimit || text.trim().length === 0
                ? 'bg-border text-text-muted cursor-not-allowed'
                : 'bg-brand text-white hover:bg-brand/90 active:scale-[0.98]'
            )}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};
